import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Grid2, Typography, useTheme } from "@mui/material";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import { getSeniority } from "./soeidRecord";
import { EMPTY_EMPLOYMENT } from "./employment";

/**
 * Bloque de datos laborales del involucrado interno, compartido por el alta y
 * la edicion.
 *
 * `columns: 2` envuelve cada campo en un Grid2 (el alta vive dentro de un
 * contenedor Grid2); `columns: 1` los suelta tal cual para el drawer, que es
 * un Stack de una sola columna.
 */
export const InvolvedEmploymentFields = ({ values, onChange, columns = 2 }) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const employment = { ...EMPTY_EMPLOYMENT, ...values };
  const handle = (field) => (value) =>
    onChange({ ...employment, [field]: value });

  const hireDate = employment.hire_date ? dayjs(employment.hire_date) : null;

  // La antiguedad se calcula al vuelo: lo que se guarda es la fecha de ingreso
  const seniority = getSeniority(employment.hire_date);
  const seniorityLabel = seniority
    ? [
        seniority.years > 0 &&
          t("involved.soeid_seniority_years", { count: seniority.years }),
        seniority.months > 0 &&
          t("involved.soeid_seniority_months", { count: seniority.months }),
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  // labelKey se pasa aparte porque no siempre coincide con la llave guardada:
  // `additional` es el SOEID y `relationship` es el puesto.
  const textField = (field, labelKey, type) => (
    <EGInput
      label={t(`involved.${labelKey}`)}
      value={employment[field]}
      onChange={(e) => handle(field)(e.target.value)}
      {...(type && { type })}
    />
  );

  const fields = [
    ["additional", textField("additional", "employee_id", "text")],
    ["area", textField("area", "area")],
    ["relationship", textField("relationship", "position")],
    ["level", textField("level", "level")],
    ["segment", textField("segment", "segment")],
    ["branch", textField("branch", "branch")],
    ["legal_vehicle", textField("legal_vehicle", "legal_vehicle")],
    ["manager", textField("manager", "manager")],
    [
      "hire_date",
      <>
        <EGDatePicker
          label={t("involved.hire_date")}
          value={hireDate}
          maxDate={dayjs()}
          onChange={(value) =>
            handle("hire_date")(
              value && dayjs(value).isValid()
                ? dayjs(value).format("YYYY-MM-DD")
                : ""
            )
          }
        />
        {seniorityLabel && (
          <Typography
            fontSize={12}
            fontWeight={600}
            color={theme.palette.blueGray.main}
            marginTop={0.5}
          >
            {t("involved.seniority")}: {seniorityLabel}
          </Typography>
        )}
      </>,
    ],
    ["corporate_email", textField("corporate_email", "corporate_email", "email")],
    ["extension", textField("extension", "extension")],
  ];

  return fields.map(([key, node]) =>
    columns === 2 ? (
      <Grid2 key={key} size={{ xs: 12, lg: 6 }}>
        {node}
      </Grid2>
    ) : (
      <Fragment key={key}>{node}</Fragment>
    )
  );
};
